import React from 'react';
import { Award, CheckCircle, Target, TrendingUp, Shield, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const ISO = () => {
  const impactos = [
    'La fidelidad del cliente.',
    'La continuidad de negocios, referencia o recomendaciones de la organización.',
    'Los resultados operativos, los ingresos y la participación en el mercado.',
    'Ventaja competitiva mediante el desarrollo de habilidades y capacidades de la organización.'
  ];

  const beneficios = [
    'Gane la confianza de las partes interesadas.',
    'Incremente la satisfacción del cliente.',
    'Acreciente la motivación de sus colaboradores.',
    'Establezca procesos corporativos que sean eficientes y efectivos.',
    'Defina claramente responsabilidades.',
    'Disminuya riesgos corporativos.',
    'Ahorre tiempo y gastos operativos.',
    'Prevea errores en lugar de corregirlos.',
    'Mejore la imagen corporativa.',
    'Reaccione de manera adecuada ante los cambios en el mercado.',
    'Fomente la mejora.'
  ];

  const valores = [
    { icon: Shield, title: 'Seguridad', description: 'Procesos certificados que garantizan la protección de tus trámites' },
    { icon: Award, title: 'Experiencia', description: 'Años de trayectoria respaldados por estándares internacionales' },
    { icon: TrendingUp, title: 'Desarrollo', description: 'Mejora continua en todos nuestros procesos y servicios' },
    { icon: Target, title: 'Valor', description: 'Servicios de calidad que superan las expectativas' },
    { icon: Users, title: 'Imagen', description: 'Reconocimiento y prestigio en el sector notarial' },
    { icon: CheckCircle, title: 'Certeza Jurídica', description: 'Garantía legal en cada documento y procedimiento' }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-notaria-primary to-notaria-secondary text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-cyan-400 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-sm px-8 py-4 rounded-full mb-8 border border-white/30">
              <Award className="w-8 h-8" />
              <span className="font-bold text-xl">Certificación ISO 9001:2015</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in leading-tight">
              La Notaría pública 90 del estado de Q. Roo. cuenta con la certificación internacional bajo la norma ISO 9001: 2015
            </h1>
            <p className="text-xl text-gray-100 mb-8 animate-fade-in">
              Que garantiza la calidad de los servicios notariales, mediante un sistema de gestión de calidad.
            </p>
          </div>
        </div>
      </section>

      {/* ISO Logo Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <img
              src="https://customer-assets.emergentagent.com/job_public-notary-lab/artifacts/q0wxzu8i_logo%20del%20ISO.png"
              alt="ISO 9001:2015"
              className="w-64 h-64 mx-auto mb-8 object-contain"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
              <p className="text-lg">
                La Norma <strong>ISO 9001: 2015</strong> promueve la adopción de un enfoque basado en procesos cuando se desarrolla, implementa y mejora la eficacia de un sistema de gestión de la calidad, para alcanzar la satisfacción del cliente mediante el cumplimiento de sus requisitos.
              </p>
              
              <p className="text-lg">
                Trabaja de manera planeada, ordenada y sistemática bajo una metodología basada en procedimientos documentados que permiten controlar de manera eficaz los servicios prestados por la Notaría, ejecuta las actividades y procesos aplicando una metodología de supervisión continua, garantizando el cumplimiento de los requisitos, con la intención de replicar las actividades en cada puesto de trabajo.
              </p>

              <p className="text-lg">
                Considera homologar de manera profesional la función notarial, buscando profesionalizar de manera consistente a sus colaboradores, desarrollando los más altos valores de <strong>honestidad, competitividad, destreza y vocación de servicio</strong>, para fortalecer el compromiso con sus clientes y la sociedad.
              </p>

              <p className="text-lg">
                Para que una organización funcione de manera eficaz, se requiere determinar y gestionar numerosas actividades relacionadas entre sí.
              </p>

              <p className="text-lg">
                Una actividad o un conjunto de actividades que utiliza recursos, y que se gestiona con el fin de permitir que los elementos de entrada se transformen en resultados altamente satisfactorios.
              </p>

              <p className="text-lg">
                El enfoque es claro, la norma en su edición ISO 9001:2015 hace énfasis en los resultados brindando mayor libertad en cómo obtenerlos. Busca optimizar la identificación y control de Riesgos y Oportunidades, dando como resultado un mayor cumplimiento de las expectativas de clientes y otras partes interesadas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-notaria-primary mb-8 text-center">
              La certificación del sistema de gestión de calidad impacta sobre
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {impactos.map((impacto, index) => (
                <div key={index} className="flex items-start gap-3 bg-gray-50 p-6 rounded-lg hover:bg-gray-100 transition-colors duration-300">
                  <CheckCircle className="w-6 h-6 text-notaria-secondary flex-shrink-0 mt-1" />
                  <p className="text-gray-800 font-medium">{impacto}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-notaria-primary to-notaria-secondary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
              La Notaría 90 de Playa del Carmen certificada bajo la norma ISO 9001:2015 ofrece:
            </h2>
            <p className="text-xl text-center mb-12 text-gray-100">
              Seguridad, experiencia, desarrollo, valor, imagen y certeza jurídica
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {valores.map((valor, index) => {
                const Icon = valor.icon;
                return (
                  <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-300">
                    <Icon className="w-12 h-12 mb-4" />
                    <h3 className="text-xl font-bold mb-2">{valor.title}</h3>
                    <p className="text-white/80">{valor.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-notaria-primary mb-12 text-center">
              Beneficios de una Certificación de Sistema de gestión ISO 9001:2015
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {beneficios.map((beneficio, index) => (
                <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 border-l-4 border-notaria-secondary">
                  <CheckCircle className="w-5 h-5 text-notaria-secondary flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{beneficio}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-notaria-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">¿Deseas conocer más sobre nuestros servicios certificados?</h2>
          <p className="text-xl mb-8 text-gray-100">Contáctanos y descubre cómo podemos ayudarte</p>
          <Link to="/contacto">
            <Button className="bg-white text-notaria-primary hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300">
              Contáctanos
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ISO;
